import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  IJupyterFhirMessage,
  IJupyterFhirMessages,
  IJupyterRegistration,
  IJupyterScopeMessage,
  IJupyterScopeMessages,
  JupyterMessageChannel,
} from "../../../@types/admin/AdminJupyterRegistration";
import { TableSorted } from "../../../components/TableSorted";
import { useRequeueJupyterMessage } from "../../../hooks/mutations/admin/jupyter";
import { useJupyterRegistrations } from "../../../hooks/queries/admin/jupyter";
import { useToast } from "../../../hooks/useToast";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminBreadcrumb,
  FilterButton,
  IAdminBreadcrumbItem,
  ModalContainer,
  ModalFormActions,
  ModalHeader,
  ModalTitle,
  StatusBadge,
  TableContainer,
  TableWrapper,
} from "../shared";
import {
  DrillDownCaption,
  MessageErrorNotice,
  PayloadContainer,
} from "./AdminJupyterIntegration.styles";

/** Which half of a registration is being explored. */
export type JupyterChannel = "scopes" | "fhir";

interface IJupyterDrillDownModalProps {
  registration: IJupyterRegistration;
  channel: JupyterChannel;
  onClose: () => void;
}

/** A scope or survey group, flattened to the fields both share. */
interface IGroupRow {
  code: string;
  lastSyncTimestamp?: string;
  messageCount: number;
}

/** A message row, flattened the same way. */
interface IMessageRow {
  /** Outbox row id — what the Requeue endpoint takes. */
  id: string;
  /** cms measurement id (scopes) or survey answer id (FHIR). */
  reference: string;
  /** Measurement time (scopes) or authored time (FHIR). */
  occurredAt?: string;
  status: string;
  attempts: number;
  lastAttemptedAt?: string;
  sentAt?: string;
  lastError?: string;
  nextAttemptAt?: string;
  payload?: unknown;
}

const CHANNEL_LABELS: Record<
  JupyterChannel,
  {
    title: string;
    codeHeader: string;
    referenceHeader: string;
    occurredHeader: string;
  }
> = {
  scopes: {
    title: "Scopes",
    codeHeader: "Scope code",
    referenceHeader: "Cms measurement ID",
    occurredHeader: "Measurement time",
  },
  fhir: {
    title: "FHIR",
    codeHeader: "Survey code",
    referenceHeader: "Survey answer ID",
    occurredHeader: "Authored at",
  },
};

/** The outbox each channel's messages live in, for the Requeue endpoint. */
const CHANNEL_TO_MESSAGE_CHANNEL: Record<
  JupyterChannel,
  JupyterMessageChannel
> = {
  scopes: "Observation",
  fhir: "Questionnaire",
};

/** Only rows the sync has given up on (or is backing off) can be handed back to it. */
export function canRequeueMessage(status: string): boolean {
  return status === "Failed" || status === "PermanentlyFailed";
}

function toScopeMessageRow(message: IJupyterScopeMessage): IMessageRow {
  return {
    id: message.id,
    reference: String(message.cmsMeasurementId),
    occurredAt: message.measurementTime,
    status: message.status,
    attempts: message.attempts,
    lastAttemptedAt: message.lastAttemptedAt,
    sentAt: message.sentAt,
    lastError: message.lastError,
    nextAttemptAt: message.nextAttemptAt,
    payload: message.payload,
  };
}

function toFhirMessageRow(message: IJupyterFhirMessage): IMessageRow {
  return {
    id: message.id,
    reference: message.surveyAnswerId,
    occurredAt: message.authoredAt,
    status: message.status,
    attempts: message.attempts,
    lastAttemptedAt: message.lastAttemptedAt,
    sentAt: message.sentAt,
    lastError: message.lastError,
    nextAttemptAt: message.nextAttemptAt,
    payload: message.payload,
  };
}

/** Green when delivered, red once it has failed for good, amber in between. */
export function messageStatusTone(
  status: string
): "active" | "warning" | "error" | "inactive" {
  switch (status) {
    case "Sent":
      return "active";
    case "Failed":
      return "warning";
    case "PermanentlyFailed":
      return "error";
    default:
      return "inactive";
  }
}

function formatPayload(payload: unknown): string {
  if (payload === null || payload === undefined) return "No payload recorded.";
  if (typeof payload === "string") {
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
      return payload;
    }
  }
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}

/**
 * The three-level drill-down for one registration, migrated from Retool's
 * `modalFrame_scopes` → `modalFrame_scopeMessages` →
 * `modalFrame_scopeMessagePayload` chain (and its FHIR twin).
 *
 * Retool stacked six modals; the portal has a single global modal slot, so
 * the levels live inside one modal and are navigated with a breadcrumb. All
 * the data is already in the registration row — nothing here fetches.
 */
export function JupyterDrillDownModal({
  registration: initialRegistration,
  channel,
  onClose,
}: IJupyterDrillDownModalProps) {
  const labels = CHANNEL_LABELS[channel];

  // The prop is the row the page had when the modal opened. Statuses move —
  // a requeue here, a sync run elsewhere — so read the live row from the shared
  // registrations query and fall back to the snapshot until it arrives.
  // Refresh lives in the table header (TableWrapper refreshKeys) and a requeue
  // invalidates this query, so the modal carries no Refresh button of its own.
  const { registrations } = useJupyterRegistrations({ refetchOnMount: false });
  const registration =
    registrations.find(
      (r) => r.integrationId === initialRegistration.integrationId
    ) ?? initialRegistration;

  const groups: IGroupRow[] = useMemo(() => {
    if (channel === "scopes") {
      return registration.scopes.map((scope: IJupyterScopeMessages) => ({
        code: scope.scopeCode,
        lastSyncTimestamp: scope.lastSyncTimestamp,
        messageCount: scope.messages.length,
      }));
    }
    return registration.fhir.map((survey: IJupyterFhirMessages) => ({
      code: survey.surveyCode,
      lastSyncTimestamp: survey.lastSyncTimestamp,
      messageCount: survey.messages.length,
    }));
  }, [registration, channel]);

  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<IMessageRow | null>(
    null
  );
  // Set when the open message was just requeued here: its local snapshot (Pending,
  // 0 attempts) wins over a cached row that still shows it failed — e.g. when the
  // refetch after the requeue failed — so Requeue is not offered a second time.
  const [requeuedMessageId, setRequeuedMessageId] = useState<string | null>(
    null
  );
  const { showToast } = useToast();
  const { requeueMessage, isRequeueing } = useRequeueJupyterMessage();

  // EN-5231 follow-up: hand a Failed / PermanentlyFailed row back to the sync.
  // The registration prop is a snapshot from the page, so the selected message
  // is updated locally as well as through the query invalidation.
  const handleRequeue = async () => {
    if (!selectedMessage) return;
    try {
      const result = await requeueMessage({
        channel: CHANNEL_TO_MESSAGE_CHANNEL[channel],
        messageId: selectedMessage.id,
      });
      setSelectedMessage({
        ...selectedMessage,
        status: result.status,
        attempts: 0,
        nextAttemptAt: result.nextAttemptAt,
        sentAt: undefined,
      });
      setRequeuedMessageId(selectedMessage.id);
      showToast(
        "Message requeued. The next sync run for this patient's ward will deliver it.",
        "success"
      );
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error requeueing the message."),
        "error"
      );
    }
  };

  const messages: IMessageRow[] = useMemo(() => {
    if (selectedCode === null) return [];
    if (channel === "scopes") {
      const scope = registration.scopes.find(
        (s) => s.scopeCode === selectedCode
      );
      return (scope?.messages ?? []).map(toScopeMessageRow);
    }
    const survey = registration.fhir.find((f) => f.surveyCode === selectedCode);
    return (survey?.messages ?? []).map(toFhirMessageRow);
  }, [registration, channel, selectedCode]);

  const selectedGroup = groups.find((group) => group.code === selectedCode);

  // The open message follows the live registrations (matched by outbox id), so a
  // sync run elsewhere shows up here. The one exception is a message requeued in
  // this modal: until the cache stops showing it as failed, the local snapshot
  // is the truth.
  const cachedSelectedMessage = selectedMessage
    ? messages.find((m) => m.id === selectedMessage.id)
    : undefined;
  const cacheIsStaleForRequeue =
    selectedMessage !== null &&
    requeuedMessageId === selectedMessage.id &&
    cachedSelectedMessage !== undefined &&
    canRequeueMessage(cachedSelectedMessage.status);
  const liveSelectedMessage =
    selectedMessage && !cacheIsStaleForRequeue
      ? cachedSelectedMessage ?? selectedMessage
      : selectedMessage;

  // EN-5230: the groups table only ever lists scopes we sync. Name the consented
  // ones we do not, so the panel does not read as complete when it is not.
  const unsyncedScopes =
    channel === "scopes" ? registration.unsyncedConsentedScopes ?? [] : [];

  const groupColumns: ColumnDef<IGroupRow, string>[] = useMemo(
    () => [
      {
        accessorKey: "code",
        header: labels.codeHeader,
        cell: ({ row }) => <span>{row.original.code}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "lastSyncTimestamp",
        header: "Last sync timestamp",
        cell: ({ row }) => (
          <span>
            {getLongFormattedDate(row.original.lastSyncTimestamp, "-")}
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "messageCount",
        header: "Messages",
        cell: ({ row }) => <span>{row.original.messageCount}</span>,
        enableSorting: true,
      },
    ],
    [labels.codeHeader]
  );

  const messageColumns: ColumnDef<IMessageRow, string>[] = useMemo(
    () => [
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge status={messageStatusTone(row.original.status)}>
            {row.original.status}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "reference",
        header: labels.referenceHeader,
        cell: ({ row }) => <span>{row.original.reference}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "occurredAt",
        header: labels.occurredHeader,
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.occurredAt, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "attempts",
        header: "Attempts",
        cell: ({ row }) => <span>{row.original.attempts}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "lastAttemptedAt",
        header: "Last attempted at",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.lastAttemptedAt, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "sentAt",
        header: "Sent at",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.sentAt, "-")}</span>
        ),
        enableSorting: true,
      },
    ],
    [labels.referenceHeader, labels.occurredHeader]
  );

  // Same widget as the page breadcrumbs, driven by component state rather
  // than routes — the levels live inside this one modal.
  const trail: IAdminBreadcrumbItem[] = [
    {
      label: labels.title,
      onClick: () => {
        setSelectedCode(null);
        setSelectedMessage(null);
      },
    },
    ...(selectedCode !== null
      ? [{ label: selectedCode, onClick: () => setSelectedMessage(null) }]
      : []),
    ...(liveSelectedMessage ? [{ label: "Payload" }] : []),
  ];

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>
          {labels.title} —{" "}
          {registration.patientName || `Patient ${registration.patientId}`}
        </ModalTitle>
        <AdminBreadcrumb items={trail} ariaLabel="Drill-down path" />
      </ModalHeader>

      {liveSelectedMessage ? (
        <>
          {liveSelectedMessage.lastError && (
            <MessageErrorNotice>
              Last error: {liveSelectedMessage.lastError}
            </MessageErrorNotice>
          )}
          {liveSelectedMessage.nextAttemptAt && (
            <DrillDownCaption>
              Next attempt{" "}
              {getLongFormattedDate(liveSelectedMessage.nextAttemptAt)}.
            </DrillDownCaption>
          )}
          <PayloadContainer>
            {formatPayload(liveSelectedMessage.payload)}
          </PayloadContainer>
        </>
      ) : (
        <>
          {selectedCode !== null && selectedGroup && (
            <DrillDownCaption>
              Last synced{" "}
              {getLongFormattedDate(selectedGroup.lastSyncTimestamp, "never")}.
              Select a message to see its payload.
            </DrillDownCaption>
          )}
          {selectedCode === null && unsyncedScopes.length > 0 && (
            <MessageErrorNotice data-testid="jhe-unsynced-scopes">
              Consented but not synced (no registry entry in the API):{" "}
              {unsyncedScopes.join(", ")}
            </MessageErrorNotice>
          )}
          <TableContainer>
            <TableWrapper refreshKeys={[["admin-jupyter-registrations"]]}>
              {selectedCode === null ? (
                <TableSorted
                  columns={groupColumns}
                  data={groups}
                  onRowClick={(group: IGroupRow) => setSelectedCode(group.code)}
                  emptyStateText="No rows found"
                />
              ) : (
                <TableSorted
                  columns={messageColumns}
                  data={messages}
                  onRowClick={(message: IMessageRow) =>
                    setSelectedMessage(message)
                  }
                  emptyStateText="No rows found"
                />
              )}
            </TableWrapper>
          </TableContainer>
        </>
      )}

      <ModalFormActions>
        {liveSelectedMessage &&
        canRequeueMessage(liveSelectedMessage.status) ? (
          <FilterButton
            variant="primary"
            onClick={handleRequeue}
            disabled={isRequeueing}
            data-testid="jhe-requeue-message"
          >
            {isRequeueing ? "Requeueing…" : "Requeue message"}
          </FilterButton>
        ) : null}
        <FilterButton variant="secondary" onClick={onClose}>
          Close
        </FilterButton>
      </ModalFormActions>
    </ModalContainer>
  );
}
